import { duplicateSequenceName, namesFromRecord } from "./uniqueNames.js";

function cloneStructured(value) {
  if (value == null) return value;
  try {
    return structuredClone(value);
  } catch {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return value;
    }
  }
}

/**
 * Duplicate a body and optionally its full child branch.
 *
 * @param {object} params
 * @param {string} params.sourceBodyUuid
 * @param {string | null} params.targetParentUuid
 * @param {boolean} [params.includeBranch=false]
 * @param {Function} params.getSceneState
 * @param {object} params.sceneActions
 * @returns {string | null}
 */
export function duplicateBodySubtree({
  sourceBodyUuid,
  targetParentUuid,
  includeBranch = false,
  getSceneState,
  sceneActions,
}) {
  const jointUuidMap = new Map();
  const siteUuidMap = new Map();
  const geomUuidMapByNewBody = new Map();
  const geomUuidMap = new Map();
  const tendonUuidMap = new Map();

  const duplicateFromSource = (currentSourceUuid, currentTargetParentUuid, withBranch) => {
    const sceneState = getSceneState();
    const sourceBody = sceneState?.bodies?.[currentSourceUuid];
    if (!sourceBody) return null;
    const nextBodyName = duplicateSequenceName(
      sourceBody.name,
      namesFromRecord(sceneState?.bodies),
      "_",
      "body"
    );
    const sourceWorldBody = !currentTargetParentUuid
      ? (sceneState?.worldBodies || []).find((worldBody) => {
          const uuid = typeof worldBody === "string" ? worldBody : worldBody?.uuid;
          return uuid === currentSourceUuid;
        })
      : null;
    const nextWorldBodyName = sourceWorldBody
      ? duplicateSequenceName(
          typeof sourceWorldBody === "string" ? sourceBody.name : sourceWorldBody?.name,
          (sceneState?.worldBodies || [])
            .map((worldBody) => (typeof worldBody === "string" ? null : worldBody?.name))
            .filter(Boolean),
          " ",
          "Object"
        )
      : null;

    const clonedInertia = sourceBody.inertia
      ? (() => {
          const inertia = cloneStructured(sourceBody.inertia);
          if (inertia && typeof inertia === "object") delete inertia.uuid;
          return inertia;
        })()
      : undefined;

    const clonedFrames = Array.isArray(sourceBody.frames)
      ? sourceBody.frames.map((frame) => {
          const cloned = cloneStructured(frame);
          if (cloned && typeof cloned === "object") delete cloned.uuid;
          return cloned;
        })
      : undefined;

    const newBodyUuid = sceneActions.addBody(
      {
        name: nextBodyName,
        position: cloneStructured(sourceBody.position) ?? [0, 0, 0],
        orientation: cloneStructured(sourceBody.orientation) ?? [1, 0, 0, 0],
        isMocap: sourceBody.isMocap,
        gravityCompensation: sourceBody.gravityCompensation,
        inertia: clonedInertia,
        frames: clonedFrames,
        geoms: [],
        joints: [],
        sites: [],
        children: [],
      },
      currentTargetParentUuid
    );
    if (nextWorldBodyName && typeof sceneActions.setWorldBodyDisplayName === "function") {
      sceneActions.setWorldBodyDisplayName(newBodyUuid, nextWorldBodyName);
    }

    (sourceBody.joints || []).forEach((jointUuid) => {
      const joint = sceneState?.joints?.[jointUuid];
      if (!joint) return;
      const clonedJoint = cloneStructured(joint);
      if (clonedJoint && typeof clonedJoint === "object") delete clonedJoint.uuid;
      const newJointUuid = sceneActions.addJoint(clonedJoint, newBodyUuid);
      if (newJointUuid) jointUuidMap.set(String(jointUuid), String(newJointUuid));
    });

    (sourceBody.sites || []).forEach((siteUuid) => {
      const site = sceneState?.sites?.[siteUuid];
      if (!site) return;
      const clonedSite = cloneStructured(site);
      if (clonedSite && typeof clonedSite === "object") delete clonedSite.uuid;
      const newSiteUuid = sceneActions.addSite(clonedSite, newBodyUuid);
      if (newSiteUuid) siteUuidMap.set(String(siteUuid), String(newSiteUuid));
    });

    const bodyGeomUuidMap = new Map();
    (sourceBody.geoms || []).forEach((geom) => {
      const clonedGeom = cloneStructured(geom);
      if (clonedGeom && typeof clonedGeom === "object") delete clonedGeom.uuid;
      const newGeomUuid = sceneActions.addGeom(clonedGeom, newBodyUuid);
      if (geom?.uuid && newGeomUuid) {
        bodyGeomUuidMap.set(String(geom.uuid), String(newGeomUuid));
        geomUuidMap.set(String(geom.uuid), String(newGeomUuid));
      }
    });
    if (bodyGeomUuidMap.size > 0) {
      geomUuidMapByNewBody.set(String(newBodyUuid), bodyGeomUuidMap);
    }

    if (withBranch) {
      (sourceBody.children || []).forEach((childUuid) => {
        duplicateFromSource(childUuid, newBodyUuid, true);
      });
    }

    return newBodyUuid;
  };

  const duplicatedRootUuid = duplicateFromSource(sourceBodyUuid, targetParentUuid, includeBranch);
  if (!duplicatedRootUuid) return null;

  if (typeof sceneActions.pairGeoms === "function") {
    const sceneState = getSceneState();
    const pairedKeys = new Set();
    geomUuidMapByNewBody.forEach((geomUuidMap, bodyUuid) => {
      const body = sceneState?.bodies?.[bodyUuid];
      if (!body || !Array.isArray(body.geoms)) return;
      body.geoms.forEach((geom) => {
        const partnerUuid = geom?.pairedGeomUUID
          ? geomUuidMap.get(String(geom.pairedGeomUUID))
          : null;
        if (!geom?.uuid || !partnerUuid) return;
        const geomUuid = String(geom.uuid);
        const pairKey = [geomUuid, partnerUuid].sort().join(":");
        if (pairedKeys.has(pairKey)) return;
        pairedKeys.add(pairKey);
        sceneActions.pairGeoms(bodyUuid, geomUuid, partnerUuid);
      });
    });
  }

  if (typeof sceneActions.addTendon === "function") {
    const sceneState = getSceneState();
    const newJointUuids = new Set(jointUuidMap.values());
    const newSiteUuids = new Set(siteUuidMap.values());
    const newGeomUuids = new Set(geomUuidMap.values());
    Object.values(sceneState?.tendons || {}).forEach((tendon) => {
      const clonedTendon = cloneStructured(tendon);
      if (!clonedTendon || typeof clonedTendon !== "object") return;
      if (Array.isArray(clonedTendon.joints) && clonedTendon.joints.length > 0) {
        const remappedJoints = clonedTendon.joints.map((entry) => {
          const sourceJointUuid = entry?.joint ? String(entry.joint) : "";
          const newJointUuid = sourceJointUuid ? jointUuidMap.get(sourceJointUuid) : null;
          return newJointUuid ? { ...entry, joint: newJointUuid } : null;
        });
        if (remappedJoints.some((entry) => !entry)) return;
        clonedTendon.joints = remappedJoints;
      }
      if (Array.isArray(clonedTendon.spatial) && clonedTendon.spatial.length > 0) {
        const remappedSpatial = clonedTendon.spatial.map((entry) => {
          if (!entry || typeof entry !== "object") return entry;
          if (entry.kind === "site") {
            const newSiteUuid = entry.site ? siteUuidMap.get(String(entry.site)) : null;
            return newSiteUuid ? { ...entry, site: newSiteUuid } : null;
          }
          if (entry.kind === "geom") {
            const newGeomUuid = entry.geom ? geomUuidMap.get(String(entry.geom)) : null;
            const newSideSiteUuid = entry.sidesite
              ? siteUuidMap.get(String(entry.sidesite))
              : undefined;
            if (!newGeomUuid || (entry.sidesite && !newSideSiteUuid)) return null;
            return {
              ...entry,
              geom: newGeomUuid,
              ...(newSideSiteUuid ? { sidesite: newSideSiteUuid } : {}),
            };
          }
          return entry;
        });
        if (remappedSpatial.some((entry) => !entry)) return;
        clonedTendon.spatial = remappedSpatial;
      }
      const hasRemappedJoint = Array.isArray(clonedTendon.joints)
        ? clonedTendon.joints.some(
            (entry) => entry?.joint && newJointUuids.has(String(entry.joint))
          )
        : false;
      const hasRemappedSpatial = Array.isArray(clonedTendon.spatial)
        ? clonedTendon.spatial.some(
            (entry) =>
              (entry?.site && newSiteUuids.has(String(entry.site))) ||
              (entry?.geom && newGeomUuids.has(String(entry.geom)))
          )
        : false;
      if (!hasRemappedJoint && !hasRemappedSpatial) return;
      delete clonedTendon.uuid;
      const newTendonUuid = sceneActions.addTendon(clonedTendon);
      if (tendon?.uuid && newTendonUuid)
        tendonUuidMap.set(String(tendon.uuid), String(newTendonUuid));
    });
  }

  if (
    typeof sceneActions.addActuator === "function" &&
    (jointUuidMap.size > 0 || tendonUuidMap.size > 0)
  ) {
    const sceneState = getSceneState();
    Object.values(sceneState?.actuators || {}).forEach((actuator) => {
      const sourceJointUuid = actuator?.joint ? String(actuator.joint) : "";
      const newJointUuid = sourceJointUuid ? jointUuidMap.get(sourceJointUuid) : null;
      const sourceTendonUuid = actuator?.tendonUuid ? String(actuator.tendonUuid) : "";
      const newTendonUuid = sourceTendonUuid ? tendonUuidMap.get(sourceTendonUuid) : null;
      if (!newJointUuid && !newTendonUuid) return;
      const clonedActuator = cloneStructured(actuator);
      if (!clonedActuator || typeof clonedActuator !== "object") return;
      delete clonedActuator.uuid;
      if (newJointUuid) clonedActuator.joint = newJointUuid;
      if (newTendonUuid) {
        clonedActuator.tendonUuid = newTendonUuid;
        const newTendonName = getSceneState()?.tendons?.[newTendonUuid]?.name;
        if (newTendonName) clonedActuator.tendon = newTendonName;
      }
      sceneActions.addActuator(clonedActuator);
    });
  }

  return duplicatedRootUuid;
}
